import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';
import config from '@/config';
import { saveGlobalElementInSession }  from 'shared-lib/helpers/local-storage';

import navigationModules from '@/store/dictionaries/navigationModules';
import { getRoutesFlatten } from '@/router/helpers/routes';

/* ADMIN */
import adminRoutes from '@/router/admin';

/* LOGIN */
const Login = () => import(/* webpackChunkName: "login" */ '@/views/Login');

/* RESET PASSWORD */
const SendResetLink = () => import(/* webpackChunkName: "resetPassword" */ '@/views/resetPassword/SendResetLink');
const SetNewPassword = () => import(/* webpackChunkName: "setNewPassword" */ '@/views/resetPassword/SetNewPassword');

/* Error Pages */
const Page404 = () => import(/* webpackChunkName: "page404" */ '@/views/Page404');
const Page500 = () => import(/* webpackChunkName: "page500" */ '@/views/Page500');

const baseRoutes = [
    {
        path: '/login',
        name: 'Login',
        component: Login,
        meta: {
            auth: false,
            title: 'Login',
        },
    },
    {
        path: '/reset-password',
        name: 'resetPassword',
        component: SendResetLink,
        meta: {
            auth: false,
            title: 'Reset password',
        },
    },
    {
        path: '/set-new-password',
        name: 'setNewPassword',
        component: SetNewPassword,
        props: (route) => ({
            token: route.query.token,
        }),
        meta: {
            auth: false,
            title: 'Set new password',
        },
    },
    {
        path: '/404',
        name: 'Page404',
        component: Page404,
        meta: {
            auth: false,
        },
    },
    {
        path: '/500',
        name: 'Page500',
        component: Page500,
        meta: {
            auth: false,
        },
    },
];

const routes = baseRoutes.concat(adminRoutes, [{
    path: '*',
    redirect: '/',
}]);

Vue.use(Router);

const router = new Router({
    mode: 'history',
    scrollBehavior: () => ({
        y: 0,
    }),
    routes,
    routesFlatten: getRoutesFlatten(routes),
});

const beforeEach = (to, from, next) => {
    const forbiddenRoutesForLoggedInUser = [ 'Login', 'resetPassword', 'setNewPassword' ];

    store.state.auth.ready
        .then(() => {
            const nearestPageWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);
            document.title = nearestPageWithTitle ? `${nearestPageWithTitle.meta.title} | ${config.pageTitle}` : config.pageTitle;

            const firstNavigationModule = navigationModules.find(item => store.getters.currentUserHasModulePrivileges(item.permissionsModuleName));
            const permissionsModuleNameWithPermission = to.meta.permissionsModuleName + (to.meta.permission ? '.' + to.meta.permission : '');

            // TODO: make all router's paths using new way of privileges management and then get rid of 'permissionsModuleName' and 'currentUserHasModulePrivileges'
            const privilegeModule = to.meta.privilegeModule;
            const permission = to.meta.permission;
            const customGuard = to.meta.customGuard;

            if (forbiddenRoutesForLoggedInUser.includes(to.name) && store.getters.auth.isLogged) {
                next({ path: '/' });
            } else if (!to.meta.auth) {
                next();
            } else if (to.meta.auth && !store.getters.auth.isLogged) {
                let query = null;
                if ( window.location.pathname !== '/login') {
                    query = { redirect: window.location.pathname + window.location.search };
                }
                next({ path: '/login', query });
            } else if (to.meta.isAdmin && !store.getters.isCurrentUserAdmin) {
                next({ path: '/' });
            } else if (to.meta.isSupport && !store.getters.isCurrentUserSupport) {
                next({ path: '/' });
            } else if (to.meta.ignorePrivilege) {
                next();
            } else if (privilegeModule && !store.getters.currentUserHasModuleAccess(privilegeModule, permission)) {
                next({ name: firstNavigationModule?.name });
            } else if (!privilegeModule && !store.getters.currentUserHasModulePrivileges(permissionsModuleNameWithPermission)) {
                next({ name: firstNavigationModule?.name });
            } else if (customGuard && !customGuard?.(store)) {
                next({ name: firstNavigationModule?.name });
            } else {
                next();
            }
        });
};

const afterEach = (to) => {
    saveGlobalElementInSession('lastRoute', to.name);
    const isNavigationModuleRoute = navigationModules.find(item => item.name === to.name);
    isNavigationModuleRoute && saveGlobalElementInSession('lastNavigationModuleRoute', to.name);
};

router.beforeEach(beforeEach);
router.afterEach(afterEach);

export default router;

export {
    routes,
    beforeEach,
};
